import { motion } from "framer-motion";

const steps = [
  {
    number: "01",
    title: "Discovery & Strategy",
    description:
      "We dive deep into your business challenges, understand your data landscape, and define clear AI objectives aligned with your goals.",
  },
  {
    number: "02",
    title: "AI Architecture & Planning",
    description:
      "Our team designs the optimal AI architecture, selects appropriate models, and creates a detailed implementation roadmap.",
  },
  {
    number: "03",
    title: "Model Development & Engineering",
    description:
      "We build, train, and fine-tune AI models using cutting-edge techniques and your proprietary data for maximum accuracy.",
  },
  {
    number: "04",
    title: "Integration & Deployment",
    description:
      "Seamless integration with your existing systems and deployment to production with robust monitoring and failsafes.",
  },
  {
    number: "05",
    title: "Optimization & Scaling",
    description:
      "Continuous performance optimization, model retraining, and infrastructure scaling as your needs grow.",
  },
  {
    number: "06",
    title: "Ongoing Support",
    description:
      "Dedicated support team, regular model updates, and proactive maintenance to ensure your AI solutions perform at their best.",
  },
];

const ProcessSection = () => {
  return (
    <section
      id="process"
      className="py-24 md:py-32 relative"
      data-testid="process-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-jetbrains text-xs tracking-widest uppercase text-neon-cyan mb-4">
            Our Process
          </p>
          <h2 className="section-title mb-6">
            From Idea to
            <br />
            <span className="text-gradient">Production AI</span>
          </h2>
          <p className="section-subtitle max-w-2xl mx-auto">
            A proven, systematic approach to building AI solutions that deliver
            real business value—from initial concept to scaled deployment.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-electric-violet via-neon-cyan to-electric-violet opacity-30" />

          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-start gap-8 ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
                data-testid={`process-step-${index}`}
              >
                {/* Timeline Node */}
                <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-void border-2 border-electric-violet flex items-center justify-center z-10">
                  <span className="font-jetbrains text-sm text-electric-violet">
                    {step.number}
                  </span>
                </div>

                {/* Content Card */}
                <div
                  className={`ml-20 md:ml-0 md:w-[calc(50%-3rem)] ${
                    index % 2 === 0 ? "md:pr-12" : "md:pl-12"
                  }`}
                >
                  <div className="glass-card p-6 hover:border-electric-violet/30 transition-colors">
                    <h3 className="font-unbounded font-medium text-xl text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Empty space for alternating layout */}
                <div className="hidden md:block md:w-[calc(50%-3rem)]" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
